import {Text} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useBoolean, suffixTestId, TestIdProps} from 'shared/web';

import {Dialog} from '../Dialog/Dialog';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';

const CONFIRM_BUTTON_TEST_ID = 'confirmDialog-confirm';

export interface ConfirmDialogProps extends TestIdProps {
  isOpen: boolean;
  text?: string;
  isLoading?: boolean;
  id?: string;
  onClose?: () => void;
  /**
   * @about You can return a promise from onConfirm. Dialog
   * will wait for the promise to finish and will close then.
   * Also, cancel/submit buttons will be disabled while
   * the promise is pending.
   * @example onConfirm: async () => await confirmSomething()
   */
  onConfirm: () => void | Promise<unknown>;
  onCloseComplete?: () => void;
  /**
   * @about overrides z-index of the dialog so that it can be displayed above the lightbox
   */
  isInLightbox?: boolean;
  /**
   * @about Focuses the confirm button instead of the close button when the
   * dialog opens, so Enter confirms directly. Opt-in: keeps the safe
   * cancel-first default for existing dialogs.
   */
  shouldFocusConfirmButton?: boolean;
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  const [isLoading, startLoading, stopLoading] = useBoolean(false);
  // Programmatic focus does not trigger :focus-visible, so the initially
  // focused confirm button gets an explicit outline until it loses focus
  const [isConfirmButtonFocused, , unsetConfirmButtonFocused] = useBoolean(
    props.shouldFocusConfirmButton ?? false
  );

  const t = useTranslationContext();

  const handleConfirm = async () => {
    startLoading();
    await props.onConfirm();
    stopLoading();
    props.onClose?.();
  };

  const confirmButtonTestId = isNilOrEmpty(suffixTestId(CONFIRM_BUTTON_TEST_ID, props))
    ? CONFIRM_BUTTON_TEST_ID
    : suffixTestId(CONFIRM_BUTTON_TEST_ID, props);

  return (
    <Dialog
      size="small"
      id={props.id}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onCloseComplete={props.onCloseComplete}
      isInLightbox={props.isInLightbox}
      initialFocusTestId={props.shouldFocusConfirmButton ? confirmButtonTestId : undefined}
      buttons={[
        {
          variant: 'secondary',
          onClick: props.onClose,
          isDisabled: isLoading || props.isLoading,
          title: t('general.actions.cancel'),
          'data-testid': suffixTestId('confirmDialog-cancel', props),
        },
        {
          variant: 'primary',
          onClick: handleConfirm,
          isLoading: isLoading || props.isLoading,
          isFocused: isConfirmButtonFocused,
          onBlur: unsetConfirmButtonFocused,
          title: t('general.actions.confirm'),
          'data-testid': confirmButtonTestId,
        },
      ]}
      data-testid={suffixTestId('confirmDialog', props)}
    >
      <Text>{props.text ?? t('general.actions.confirmNeutralQuestion')}</Text>
    </Dialog>
  );
}
